import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  approveScene,
  animateScene,
  generateAllScenes,
  getMaterializeThumbnailStatus,
  getSceneApprovalState,
  launchIdeaVideo,
  loadIdeaBank,
  materializeIdea,
  refreshChannelStyleAudit,
  regenerateScene,
  renderAutoVideo,
  replaceSceneImage,
  setChannelVoice,
  stitchAnimatedScenes,
  unapproveScene,
  uploadMaterializeThumbnail,
  setSceneAnimationPrompt,
} from "@/lib/api";
import SceneApprovalList from "./SceneApprovalList";
import CharacterBibleEditor from "./CharacterBibleEditor";

/**
 * Two-stage modal for the "▶ Next Video" flow on an Active Channel.
 *
 * Stage 1 — `view: "bank"`:
 *   Show 20 fresh idea cards Claude generated (cached 24h). User picks
 *   one or hits "Re-roll" to get a fresh batch.
 *
 * Stage 2 — `view: "packet"`:
 *   For the picked idea, we generate a SEO-tuned title + description
 *   + a thumbnail in the channel's existing style. Show those outputs
 *   with copy buttons + a "▶ Auto-create the video" CTA that kicks
 *   off the auto-video pipeline.
 */
export default function IdeaBankModal({ channel, onClose }) {
  const navigate = useNavigate();
  const [view, setView] = useState("bank");
  const [bank, setBank] = useState([]);
  const [bankMeta, setBankMeta] = useState({});
  const [bankLoading, setBankLoading] = useState(true);
  const [reRolling, setReRolling] = useState(false);
  const [picking, setPicking] = useState(null);  // idea_id we're materializing
  const [packet, setPacket] = useState(null);
  const [launching, setLaunching] = useState(false);
  // Scene approval state — polled in parallel with thumbnail. Lets us
  // show scene cards inline in the packet view so the user can review
  // / regenerate / upload before clicking ▶ Animate.
  const [sceneState, setSceneState] = useState(null);
  const scenePollRef = useRef(null);
  const API_BASE = (
    process.env.REACT_APP_BACKEND_URL || ""
  ).replace(/\/$/, "");
  // Channel-voice override editor — when set, every idea in the bank
  // must follow this directive instead of Claude's auto-detected
  // pattern. Hydrated from the channel doc.
  const initialVoice = channel?.channel_voice_override || "";
  const [voice, setVoice] = useState(initialVoice);
  const [savedVoice, setSavedVoice] = useState(initialVoice);
  const [voiceOpen, setVoiceOpen] = useState(Boolean(initialVoice));
  const [voiceSaving, setVoiceSaving] = useState(false);
  // Style-audit refresh — force-rebuilds the character bible + thumbnail
  // style brief by re-running Claude Vision on the channel's top 3
  // thumbnails. Surfaces what was extracted so the user can verify
  // (or report) what the AI sees.
  const [styleAuditing, setStyleAuditing] = useState(false);
  const handleRefreshStyleAudit = async () => {
    if (styleAuditing) return;
    setStyleAuditing(true);
    const tid = toast.loading("Re-auditing channel style (Claude Vision)…");
    try {
      const r = await refreshChannelStyleAudit(channel.channel_id);
      const n = r?.extracted_character_count ?? 0;
      const brief = r?.thumbnail_style_brief || {};
      if (n > 0 && !brief.fallback) {
        toast.success(
          `Style re-audited · ${n} character${n === 1 ? "" : "s"} captured · ` +
          `style brief refreshed`,
          { id: tid },
        );
      } else if (n > 0) {
        toast.warning(
          `${n} character${n === 1 ? "" : "s"} captured · style brief fell back`,
          { id: tid },
        );
      } else if (!brief.fallback) {
        toast.warning(
          "Style brief refreshed but no recurring characters found · " +
          "next thumbnails will use generic Pixar style",
          { id: tid },
        );
      } else {
        toast.error(
          "Re-audit didn't capture characters or style — " +
          "thumbnail downloads or Claude Vision may be unavailable.",
          { id: tid },
        );
      }
      // Also invalidate any cached idea bank so next generation rolls
      // with the fresh style (small UX win).
      setBank([]);
      setBankMeta({});
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Style re-audit failed",
        { id: tid },
      );
    } finally {
      setStyleAuditing(false);
    }
  };

  const load = useCallback(async (force = false) => {
    if (force) setReRolling(true); else setBankLoading(true);
    try {
      const data = await loadIdeaBank(channel.channel_id, { force });
      setBank(data.ideas || []);
      setBankMeta({
        cached: data.cached,
        cached_at: data.cached_at,
        detected_pattern: data.detected_pattern || "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load ideas");
    } finally {
      setBankLoading(false);
      setReRolling(false);
    }
  }, [channel.channel_id]);

  useEffect(() => { load(); }, [load]);

  // Thumbnail polling — the backend now generates the thumbnail in a
  // background task and returns immediately so the request stays well
  // under Cloudflare's ~100s edge timeout. We poll every 2.5s until
  // the bg task lands (or fails). Cleared when the modal closes /
  // packet view exits.
  const thumbPollRef = useRef(null);
  const stopThumbPoll = useCallback(() => {
    if (thumbPollRef.current) {
      clearInterval(thumbPollRef.current);
      thumbPollRef.current = null;
    }
  }, []);
  useEffect(() => () => stopThumbPoll(), [stopThumbPoll]);

  // Scene-state polling — separate from thumbnail since they run on
  // different background tasks (thumbnail is fast, scenes take 30-90s).
  const stopScenePoll = useCallback(() => {
    if (scenePollRef.current) {
      clearInterval(scenePollRef.current);
      scenePollRef.current = null;
    }
  }, []);
  useEffect(() => () => stopScenePoll(), [stopScenePoll]);

  const refreshSceneState = useCallback(async (jobId) => {
    if (!jobId) return;
    try {
      const s = await getSceneApprovalState(jobId);
      setSceneState(s);
    } catch (err) {
      console.debug("scene poll skipped:", err?.message);
    }
  }, []);

  const onPick = async (idea) => {
    setPicking(idea.id);
    stopThumbPoll();
    stopScenePoll();
    setSceneState(null);
    try {
      const p = await materializeIdea(channel.channel_id, idea.id);
      setPacket(p);
      setView("packet");
      if (p?.materialize_error) {
        toast.error(`Materialize failed: ${p.materialize_error}`);
        return;
      }
      // Kick off thumbnail polling if it's still generating.
      if (p?.thumbnail_status === "generating" || !p?.thumbnail_url) {
        thumbPollRef.current = setInterval(async () => {
          try {
            const s = await getMaterializeThumbnailStatus(
              channel.channel_id, idea.id,
            );
            if (s.status === "ready") {
              setPacket((prev) => prev ? {
                ...prev,
                thumbnail_url: s.thumbnail_url,
                thumbnail_status: "ready",
                thumbnail_provider: s.thumbnail_provider,
                thumbnail_error: null,
              } : prev);
              stopThumbPoll();
            } else if (s.status === "failed") {
              setPacket((prev) => prev ? {
                ...prev,
                thumbnail_status: "failed",
                thumbnail_error: s.thumbnail_error || "Generation failed",
              } : prev);
              stopThumbPoll();
            }
          } catch (err) {
            console.debug("thumbnail poll skipped:", err?.message);
          }
        }, 2500);
      }
      // Kick off scene-state polling if the materialize endpoint
      // created a job (always does for active-channel idea materialize).
      if (p?.job_id) {
        await refreshSceneState(p.job_id);
        scenePollRef.current = setInterval(async () => {
          try {
            const s = await getSceneApprovalState(p.job_id);
            setSceneState(s);
            // Stop polling once every scene reaches a terminal state
            // (approved/ready/failed). The user can still kick a
            // re-roll if they want, which restarts the poll via
            // handleRetryAllScenes.
            if (
              Array.isArray(s.scenes_status)
              && s.scenes_status.length > 0
              && s.scenes_status.every(
                (st) => st === "ready" || st === "approved" || st === "failed",
              )
            ) {
              stopScenePoll();
            }
          } catch (err) {
            console.debug("scene poll skipped:", err?.message);
          }
        }, 2500);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't materialize idea");
    } finally {
      setPicking(null);
    }
  };

  // ---- Scene approval handlers (used by the inline scene cards) ----
  const handleApproveScene = async (idx) => {
    if (!packet?.job_id) return;
    await approveScene(packet.job_id, idx);
    await refreshSceneState(packet.job_id);
  };
  const handleUnapproveScene = async (idx) => {
    if (!packet?.job_id) return;
    await unapproveScene(packet.job_id, idx);
    await refreshSceneState(packet.job_id);
  };
  const handleRegenerateScene = async (idx, prompt) => {
    if (!packet?.job_id) return;
    const tid = toast.loading(`Regenerating scene ${idx + 1}…`);
    try {
      const r = await regenerateScene(packet.job_id, idx, prompt);
      if (r?.ok) toast.success(`Scene ${idx + 1} regenerated`, { id: tid });
      else toast.error(r?.error || "Regenerate failed", { id: tid });
      await refreshSceneState(packet.job_id);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Regenerate failed", { id: tid },
      );
    }
  };
  const handleUploadSceneImage = async (idx, file) => {
    if (!packet?.job_id) return;
    const tid = toast.loading(`Uploading scene ${idx + 1}…`);
    try {
      await replaceSceneImage(packet.job_id, idx, file);
      toast.success(`Scene ${idx + 1} replaced & approved`, { id: tid });
      await refreshSceneState(packet.job_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed", { id: tid });
    }
  };
  const handleAnimateAll = async () => {
    if (!packet?.job_id) {
      toast.error("Pipeline didn't start — can't animate");
      return;
    }
    setLaunching(true);
    try {
      await renderAutoVideo(packet.job_id);
      toast.success("Animation pipeline started");
      navigate(`/auto-video/${packet.job_id}`);
    } catch (err) {
      setLaunching(false);
      toast.error(err?.response?.data?.detail || "Couldn't start animation");
    }
  };
  const handleRetryAllScenes = async () => {
    if (!packet?.job_id) return;
    try {
      await generateAllScenes(packet.job_id);
      toast.success("Retrying failed scenes…");
      // Re-arm the poll loop since some scenes flipped back to pending.
      stopScenePoll();
      scenePollRef.current = setInterval(async () => {
        try {
          const s = await getSceneApprovalState(packet.job_id);
          setSceneState(s);
          if (
            Array.isArray(s.scenes_status)
            && s.scenes_status.length > 0
            && s.scenes_status.every(
              (st) => st === "ready" || st === "approved" || st === "failed",
            )
          ) {
            stopScenePoll();
          }
        } catch (err) {
          console.debug("scene poll skipped:", err?.message);
        }
      }, 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Retry failed");
    }
  };

  // ---- Per-scene Veo3 animation handlers (mirrors AutoVideo.jsx) ----
  const [animatingScenes, setAnimatingScenes] = useState(() => new Set());
  const [animateAllBusy, setAnimateAllBusy] = useState(false);
  const animateAllAbortRef = useRef(false);
  const [stitchBusy, setStitchBusy] = useState(false);

  const refreshSceneStateNow = useCallback(async () => {
    if (!packet?.job_id) return null;
    try {
      const s = await getSceneApprovalState(packet.job_id);
      setSceneState(s);
      return s;
    } catch (err) {
      console.debug("refreshSceneStateNow skipped:", err?.message);
      return null;
    }
  }, [packet?.job_id]);

  const markAnimating = (idx, on) => {
    setAnimatingScenes((prev) => {
      const next = new Set(prev);
      if (on) next.add(idx);
      else next.delete(idx);
      return next;
    });
  };

  const waitForSceneAnim = useCallback(
    async (idx, { maxMs = 6 * 60 * 1000, intervalMs = 5000 } = {}) => {
      const deadline = Date.now() + maxMs;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, intervalMs));
        const snap = await refreshSceneStateNow();
        const st = snap?.scene_animation_status?.[idx] || "";
        if (st === "ready") return { ok: true };
        if (st === "failed") {
          return {
            ok: false,
            error: snap?.scene_animation_errors?.[idx] || "Veo3 failed",
          };
        }
      }
      return { ok: false, error: "Timed out waiting for Veo3" };
    },
    [refreshSceneStateNow],
  );

  const handleAnimateOneScene = async (idx) => {
    if (!packet?.job_id) {
      toast.error("Pipeline didn't start — can't animate");
      return;
    }
    markAnimating(idx, true);
    const tid = toast.loading(`Sending scene ${idx + 1} to Veo3…`);
    try {
      await animateScene(packet.job_id, idx);
      toast.loading(`Scene ${idx + 1} animating (8s)…`, { id: tid });
      await refreshSceneStateNow();
      const r = await waitForSceneAnim(idx);
      if (r.ok) toast.success(`Scene ${idx + 1} animated`, { id: tid });
      else toast.error(`Scene ${idx + 1}: ${r.error}`, { id: tid });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || `Couldn't animate scene ${idx + 1}`,
        { id: tid },
      );
    } finally {
      markAnimating(idx, false);
    }
  };

  const handleAnimateAllApproved = async () => {
    if (!packet?.job_id) return;
    const statuses = sceneState?.scenes_status || [];
    const animStatus = sceneState?.scene_animation_status || [];
    const queue = [];
    for (let i = 0; i < statuses.length; i += 1) {
      if (
        statuses[i] === "approved"
        && animStatus[i] !== "ready"
        && animStatus[i] !== "animating"
      ) {
        queue.push(i);
      }
    }
    if (queue.length === 0) {
      toast.info("No approved scenes left to animate");
      return;
    }
    setAnimateAllBusy(true);
    animateAllAbortRef.current = false;
    let okCount = 0;
    let failCount = 0;
    for (const idx of queue) {
      if (animateAllAbortRef.current) break;
      markAnimating(idx, true);
      try {
        await animateScene(packet.job_id, idx);
        await refreshSceneStateNow();
        const r = await waitForSceneAnim(idx);
        if (r.ok) okCount += 1;
        else {
          failCount += 1;
          toast.error(`Scene ${idx + 1}: ${r.error}`);
        }
      } catch (err) {
        failCount += 1;
        toast.error(
          err?.response?.data?.detail || `Scene ${idx + 1} failed to start`,
        );
      } finally {
        markAnimating(idx, false);
      }
    }
    setAnimateAllBusy(false);
    if (okCount > 0) {
      toast.success(
        `Animated ${okCount}/${queue.length} scene${queue.length === 1 ? "" : "s"}`,
      );
    }
    if (failCount > 0 && okCount === 0) {
      toast.error(`All ${failCount} animation${failCount === 1 ? "" : "s"} failed`);
    }
  };

  const handleStopAnimateAll = () => {
    animateAllAbortRef.current = true;
    toast.info("Stopping after current scene finishes…");
  };

  const handleStitchFinalVideo = async () => {
    if (!packet?.job_id) return;
    setStitchBusy(true);
    const tid = toast.loading("Stitching final video…");
    try {
      await stitchAnimatedScenes(packet.job_id);
      await refreshSceneStateNow();
      const deadline = Date.now() + 12 * 60 * 1000;
      let finalState = "running";
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 4000));
        const snap = await refreshSceneStateNow();
        const st = snap?.stitched_status || "";
        if (st === "ready" || st === "failed") {
          finalState = st;
          if (st === "failed") {
            toast.error(snap?.stitched_error || "Stitch failed", { id: tid });
          } else {
            toast.success("Final video ready", { id: tid });
          }
          break;
        }
      }
      if (finalState === "running") {
        toast.error("Stitch timed out — check back in a minute", { id: tid });
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't start stitch", { id: tid },
      );
    } finally {
      setStitchBusy(false);
    }
  };

  // ---- User-supplied thumbnail upload (overrides the auto-generated one) ----
  const [thumbnailUploading, setThumbnailUploading] = useState(false);

  const handleSaveAnimationPrompt = useCallback(
    async (idx, prompt) => {
      if (!packet?.job_id) return;
      try {
        const r = await setSceneAnimationPrompt(
          packet.job_id, idx, prompt || "",
        );
        setSceneState((prev) => {
          if (!prev) return prev;
          const next = { ...prev };
          const arr = (next.scene_animation_prompts || []).slice();
          while (arr.length <= idx) arr.push("");
          arr[idx] = r?.prompt || "";
          next.scene_animation_prompts = arr;
          return next;
        });
      } catch (err) {
        toast.error(
          err?.response?.data?.detail || "Couldn't save animate prompt",
        );
      }
    },
    [packet?.job_id],
  );

  // Bulk-fill per-scene Veo3 prompts from an uploaded .txt/.docx/.pdf.
  const handleBulkAnimationPromptsFromDoc = useCallback(
    async (parsed) => {
      if (!packet?.job_id || !Array.isArray(parsed) || parsed.length === 0) {
        return;
      }
      const count = sceneState?.scenes_status?.length || 0;
      if (count === 0) {
        toast.error("Scenes not ready yet — try again in a moment");
        return;
      }
      const n = Math.min(parsed.length, count);
      let okN = 0;
      for (let i = 0; i < n; i += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await handleSaveAnimationPrompt(i, parsed[i] || "");
          okN += 1;
        } catch (err) {
          // toast handled inside handleSaveAnimationPrompt.
        }
      }
      if (okN > 0) {
        toast.success(`Applied ${okN} scene prompt${okN === 1 ? "" : "s"}`);
      }
    },
    [packet?.job_id, sceneState, handleSaveAnimationPrompt],
  );
  const handleUploadThumbnail = async (file) => {
    if (!packet?.idea?.id || !channel?.channel_id) return;
    if (!file) return;
    setThumbnailUploading(true);
    const tid = toast.loading("Uploading your thumbnail…");
    try {
      const r = await uploadMaterializeThumbnail({
        channelId: channel.channel_id,
        ideaId: packet.idea.id,
        file,
      });
      setPacket((prev) => prev ? {
        ...prev,
        thumbnail_url: r.thumbnail_url,
        thumbnail_provider: r.thumbnail_provider,
        thumbnail_status: r.status,
        thumbnail_error: "",
      } : prev);
      toast.success("Thumbnail replaced", { id: tid });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Thumbnail upload failed",
        { id: tid },
      );
    } finally {
      setThumbnailUploading(false);
    }
  };

  const onLaunch = async () => {
    if (!packet) return;
    setLaunching(true);
    try {
      const r = await launchIdeaVideo(channel.channel_id, packet.idea.id);
      toast.success("Auto-video pipeline started");
      navigate(`/auto-video/${r.job_id}`);
    } catch (err) {
      setLaunching(false);
      toast.error(err?.response?.data?.detail || "Couldn't launch pipeline");
    }
  };

  const onSaveVoice = async () => {
    const trimmed = (voice || "").trim();
    if (trimmed === savedVoice.trim()) {
      toast.message("No changes to save");
      return;
    }
    setVoiceSaving(true);
    try {
      await setChannelVoice(channel.channel_id, trimmed);
      setSavedVoice(trimmed);
      // Cache was invalidated server-side. Force the next bank to
      // re-roll under the new voice directive.
      setBank([]);
      setBankMeta({});
      await load(true);
      toast.success(
        trimmed
          ? "Voice saved · Idea Bank re-rolled"
          : "Voice cleared · Idea Bank re-rolled",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save voice");
    } finally {
      setVoiceSaving(false);
    }
  };

  const onClearVoice = async () => {
    setVoice("");
    if (!savedVoice) {
      toast.message("Nothing to clear");
      return;
    }
    setVoiceSaving(true);
    try {
      await setChannelVoice(channel.channel_id, "");
      setSavedVoice("");
      setBank([]);
      setBankMeta({});
      await load(true);
      toast.success("Voice cleared · Idea Bank re-rolled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't clear voice");
    } finally {
      setVoiceSaving(false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div
      data-testid="idea-bank-overlay"
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        data-testid="idea-bank-modal"
        className="bg-zinc-950 border border-emerald-400/40 rounded-sm w-full max-w-4xl my-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-400 mb-1">
              // {view === "bank" ? "IDEA BANK · 20 FRESH IDEAS" : "READY TO LAUNCH"}
            </div>
            <h2 className="font-display text-2xl font-black text-white truncate">
              {channel.name}
            </h2>
          </div>
          <button
            data-testid="idea-bank-close"
            onClick={onClose}
            className="font-mono text-zinc-400 hover:text-emerald-400 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {view === "bank" && (
          <BankView
            bank={bank}
            loading={bankLoading}
            reRolling={reRolling}
            picking={picking}
            meta={bankMeta}
            onPick={onPick}
            onReRoll={() => load(true)}
            voice={voice}
            savedVoice={savedVoice}
            voiceOpen={voiceOpen}
            voiceSaving={voiceSaving}
            onVoiceChange={setVoice}
            onVoiceToggle={() => setVoiceOpen((v) => !v)}
            onVoiceSave={onSaveVoice}
            onVoiceClear={onClearVoice}
            styleAuditing={styleAuditing}
            onRefreshStyleAudit={handleRefreshStyleAudit}
          />
        )}

        {view === "packet" && packet && (
          <PacketView
            packet={packet}
            launching={launching}
            sceneState={sceneState}
            apiBase={API_BASE}
            onLaunch={onLaunch}
            onAnimateAll={handleAnimateAll}
            onApproveScene={handleApproveScene}
            onUnapproveScene={handleUnapproveScene}
            onRegenerateScene={handleRegenerateScene}
            onUploadScene={handleUploadSceneImage}
            onRetryAllScenes={handleRetryAllScenes}
            onAnimateScene={handleAnimateOneScene}
            onAnimateAllApproved={handleAnimateAllApproved}
            onStopAnimateAll={handleStopAnimateAll}
            animatingScenes={animatingScenes}
            animateAllBusy={animateAllBusy}
            onStitchFinalVideo={handleStitchFinalVideo}
            stitchBusy={stitchBusy}
            onUploadThumbnail={handleUploadThumbnail}
            thumbnailUploading={thumbnailUploading}
            onSaveAnimationPrompt={handleSaveAnimationPrompt}
            onBulkAnimationPromptsFromDoc={handleBulkAnimationPromptsFromDoc}
            onBack={() => {
              stopThumbPoll();
              stopScenePoll();
              setSceneState(null);
              setPacket(null);
              setView("bank");
            }}
            onCopy={copy}
          />
        )}
      </div>
    </div>
  );
}

function BankView({
  bank, loading, reRolling, picking, meta, onPick, onReRoll,
  voice, savedVoice, voiceOpen, voiceSaving,
  onVoiceChange, onVoiceToggle, onVoiceSave, onVoiceClear,
  styleAuditing, onRefreshStyleAudit,
}) {
  const voicePanel = (
    <VoicePanel
      voice={voice}
      savedVoice={savedVoice}
      open={voiceOpen}
      saving={voiceSaving}
      onChange={onVoiceChange}
      onToggle={onVoiceToggle}
      onSave={onVoiceSave}
      onClear={onVoiceClear}
    />
  );
  if (loading) {
    return (
      <>
        {voicePanel}
        <div className="text-center py-20" data-testid="idea-bank-loading">
          <div className="font-mono text-sm text-emerald-400">
            [claude] generating ideas<span className="blink">_</span>
          </div>
          <div className="font-mono text-xs text-zinc-500 mt-2">
            Checking shipped titles, building 20 non-duplicate angles
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      {voicePanel}
      {meta.detected_pattern &&
       meta.detected_pattern.toLowerCase() !== "no_clear_pattern" && (
        <div
          data-testid="idea-bank-pattern"
          className="mx-6 mt-5 border-l-2 border-emerald-400 bg-emerald-400/[0.04] px-4 py-3 rounded-r-sm"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-300 mb-1">
            PATTERN DETECTED · all 20 ideas follow this
          </div>
          <div className="text-sm text-zinc-100 leading-relaxed">
            {meta.detected_pattern}
          </div>
        </div>
      )}
      <div className="px-6 pt-5 pb-3 flex items-center justify-between">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
          {meta.cached ? "cached · refresh for fresh batch" : "fresh batch"}
        </div>
        <div className="flex items-center gap-4">
          <button
            data-testid="idea-bank-restyle"
            onClick={onRefreshStyleAudit}
            disabled={styleAuditing}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-amber-300 disabled:text-zinc-600 transition-colors"
            title="Re-run Claude Vision on this channel's top 3 thumbnails to refresh the character bible + style brief used for new thumbnails."
          >
            {styleAuditing ? "[vision] auditing…" : "👁 Re-audit style"}
          </button>
          <button
            data-testid="idea-bank-reroll"
            onClick={onReRoll}
            disabled={reRolling}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400 hover:text-emerald-300 disabled:text-zinc-500 transition-colors"
          >
            {reRolling ? "[claude] re-rolling…" : "↻ Re-roll (fresh 20)"}
          </button>
        </div>
      </div>
      <div
        data-testid="idea-bank-grid"
        className="px-6 pb-6 grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto"
      >
        {bank.map((idea) => (
          <button
            key={idea.id}
            data-testid={`idea-card-${idea.id}`}
            onClick={() => onPick(idea)}
            disabled={picking !== null}
            className={`text-left border rounded-sm p-4 transition-all bg-zinc-950 ${
              picking === idea.id
                ? "border-emerald-400 bg-emerald-400/[0.06]"
                : "border-zinc-800 hover:border-emerald-400/60 hover:bg-zinc-900"
            } disabled:cursor-wait`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-300 border border-emerald-400/30 px-1.5 py-0.5 rounded-sm">
                {idea.hook_type || "idea"}
              </span>
              <span className="font-mono text-[10px] text-zinc-500">
                ~{idea.target_seconds}s
              </span>
            </div>
            <div className="font-display text-base font-bold text-white leading-snug mb-2">
              {idea.title}
            </div>
            {idea.angle && (
              <div className="text-xs text-zinc-300 leading-relaxed">
                {idea.angle}
              </div>
            )}
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] mt-3 pt-2 border-t border-zinc-900 text-emerald-400">
              {picking === idea.id ? "[claude] generating SEO + thumb…" : "▸ Use this idea"}
            </div>
          </button>
        ))}
      </div>
    </>
  );
}

function PacketView({
  packet, launching, sceneState, apiBase,
  onLaunch, onAnimateAll,
  onApproveScene, onUnapproveScene, onRegenerateScene, onUploadScene,
  onRetryAllScenes,
  onAnimateScene, onAnimateAllApproved, onStopAnimateAll,
  animatingScenes, animateAllBusy,
  onStitchFinalVideo, stitchBusy,
  onUploadThumbnail, thumbnailUploading,
  onSaveAnimationPrompt,
  onBulkAnimationPromptsFromDoc,
  onBack, onCopy,
}) {
  return (
    <div className="p-6 space-y-5" data-testid="idea-packet">
      <button
        onClick={onBack}
        data-testid="idea-packet-back"
        className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-emerald-400 transition-colors"
      >
        ← Back to 20 ideas
      </button>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-start">
        <div className="space-y-4 min-w-0">
          <Field
            label="SEO TITLE"
            value={packet.seo_title}
            onCopy={() => onCopy(packet.seo_title, "Title")}
            testid="packet-seo-title"
          />
          <Field
            label="DESCRIPTION"
            value={packet.seo_description}
            multiline
            onCopy={() => onCopy(packet.seo_description, "Description")}
            testid="packet-seo-description"
          />
        </div>

        <div className="w-full md:w-[280px] shrink-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400 mb-1.5">
            THUMBNAIL · 16:9
          </div>
          {packet.thumbnail_url ? (
            <a
              href={packet.thumbnail_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="packet-thumbnail-link"
              className="block border border-zinc-800 hover:border-emerald-400 rounded-sm overflow-hidden transition-colors"
              title="Open full size in new tab"
            >
              <img
                src={packet.thumbnail_url}
                alt="Generated thumbnail"
                className="w-full block"
                data-testid="packet-thumbnail-img"
              />
            </a>
          ) : packet.thumbnail_status === "generating" ? (
            <div
              data-testid="packet-thumbnail-generating"
              className="border border-yellow-400/40 bg-yellow-400/[0.04] rounded-sm p-4 text-zinc-300 aspect-video flex flex-col items-center justify-center text-center gap-2"
            >
              <div className="w-8 h-8 border-2 border-yellow-400/30 border-t-yellow-400 rounded-full animate-spin" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400">
                generating thumbnail<span className="blink">_</span>
              </span>
              <span className="font-mono text-[10px] text-zinc-500 normal-case">
                OpenAI gpt-image-1 · ~10-40s
              </span>
            </div>
          ) : (
            <div
              data-testid="packet-thumbnail-empty"
              className="border border-amber-400/40 bg-amber-400/[0.04] rounded-sm p-4 text-zinc-300 font-mono text-[11px] aspect-video flex flex-col items-center justify-center text-center gap-1.5"
            >
              <span className="text-amber-300 uppercase tracking-[0.2em] text-[10px]">
                thumbnail unavailable
              </span>
              <span
                data-testid="packet-thumbnail-error"
                className="text-zinc-400 text-[10px] leading-relaxed normal-case"
              >
                {packet.thumbnail_error
                  ? `${packet.thumbnail_error}`
                  : "All image providers failed."}
              </span>
              <span className="text-zinc-500 text-[10px] mt-1">
                The auto-video pipeline can still generate one.
              </span>
            </div>
          )}
          {packet.thumbnail_provider && packet.thumbnail_provider !== "openai-gpt-image" && (
            <div
              data-testid="packet-thumbnail-provider-note"
              className="font-mono text-[9px] text-amber-400 mt-2"
            >
              ⚠ Fell back to {packet.thumbnail_provider} — primary provider was unavailable.
            </div>
          )}
          {packet.style_brief?.fallback && (
            <div className="font-mono text-[9px] text-amber-400 mt-2">
              ⚠ Used default style template — channel had no readable
              existing thumbnails to learn from.
            </div>
          )}

          {/* Upload my own — replaces the auto-generated thumbnail with
              a user-supplied image (PNG/JPG/WEBP up to 8 MB). */}
          {packet.thumbnail_status !== "generating" && (
            <div className="mt-3 flex flex-col gap-2">
              <label
                data-testid="packet-thumbnail-upload-label"
                className={`block text-center font-mono text-[10px] uppercase tracking-[0.25em] cursor-pointer border rounded-sm px-3 py-2 transition-colors ${
                  thumbnailUploading
                    ? "border-yellow-400/40 text-yellow-300 bg-yellow-400/[0.04] cursor-wait"
                    : "border-zinc-700 hover:border-cyan-400 text-zinc-300 hover:text-cyan-300 bg-black"
                }`}
                title="Replace this thumbnail with your own image (PNG / JPG / WEBP, max 8 MB)"
              >
                {thumbnailUploading
                  ? "▸ uploading…"
                  : packet.thumbnail_provider === "user-upload"
                    ? "⬆ Replace with another image"
                    : "⬆ Upload my own thumbnail"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  className="hidden"
                  data-testid="packet-thumbnail-upload-input"
                  disabled={thumbnailUploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f && onUploadThumbnail) onUploadThumbnail(f);
                    e.target.value = "";
                  }}
                />
              </label>
              {packet.thumbnail_provider === "user-upload" && (
                <div
                  data-testid="packet-thumbnail-user-upload-badge"
                  className="font-mono text-[9px] uppercase tracking-[0.2em] text-cyan-300 text-center"
                >
                  ✓ using your image
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Scene approval list — streams in as the auto-video pipeline
          generates the script, voiceover, then each scene image. The
          user reviews / replaces / approves each scene, then clicks
          ▶ Animate to kick off the per-scene Veo animation step. */}
      {packet.job_id ? (
        sceneState && Array.isArray(sceneState.scenes_status) && sceneState.scenes_status.length > 0 ? (
          <SceneApprovalList
            state={sceneState}
            apiBase={apiBase}
            onApprove={onApproveScene}
            onUnapprove={onUnapproveScene}
            onRegenerate={onRegenerateScene}
            onUpload={onUploadScene}
            onAnimateAll={onAnimateAll}
            onRetryAll={onRetryAllScenes}
            animateBusy={launching}
            onAnimateScene={onAnimateScene}
            onAnimateAllApproved={onAnimateAllApproved}
            onStopAnimateAll={onStopAnimateAll}
            animatingScenes={animatingScenes}
            animateAllBusy={animateAllBusy}
            onStitchFinalVideo={onStitchFinalVideo}
            stitchBusy={stitchBusy}
            onSaveAnimationPrompt={onSaveAnimationPrompt}
            onBulkAnimationPromptsFromDoc={onBulkAnimationPromptsFromDoc}
          />
        ) : (
          <div
            data-testid="packet-scenes-preparing"
            className="border border-zinc-800 rounded-sm py-8 px-5 text-center"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 mb-2">
              // PREPARING SCENES
            </div>
            <div className="w-8 h-8 border-2 border-yellow-400/30 border-t-yellow-400 rounded-full animate-spin mx-auto mb-3" />
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              writing script · recording voiceover · queueing scene images
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 mt-1">
              ~30-60s · scenes will appear here as they&apos;re generated
            </div>
          </div>
        )
      ) : (
        // Legacy fallback — pipeline didn't create a job (e.g.
        // pre-deploy job, or backend error during the launch step).
        // Show the original "Auto-create" button so the user can still
        // trigger the pipeline manually.
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-3 border-t border-zinc-800">
          {packet.pipeline_error && (
            <div className="font-mono text-[10px] text-amber-400 flex-1">
              ⚠ Pipeline didn&apos;t auto-start: {packet.pipeline_error}
            </div>
          )}
          <button
            data-testid="packet-launch"
            onClick={onLaunch}
            disabled={launching}
            className="font-display text-sm font-black uppercase tracking-wide bg-emerald-400 text-black hover:bg-emerald-300 disabled:bg-zinc-700 disabled:text-zinc-500 px-5 py-2.5 rounded-sm transition-colors"
          >
            {launching ? "▶ launching auto-video…" : "▶ Auto-create the video"}
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, multiline, onCopy, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400">
          {label}
        </div>
        <button
          data-testid={`${testid}-copy`}
          onClick={onCopy}
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-emerald-400 transition-colors"
        >
          ⧉ copy
        </button>
      </div>
      {multiline ? (
        <pre className="font-sans text-sm text-zinc-100 leading-relaxed bg-black/40 border border-zinc-900 rounded-sm px-4 py-3 whitespace-pre-wrap max-h-64 overflow-y-auto">
          {value}
        </pre>
      ) : (
        <div className="font-display text-base text-white bg-black/40 border border-zinc-900 rounded-sm px-4 py-3">
          {value}
        </div>
      )}
    </div>
  );
}


/**
 * Collapsible "Channel voice" panel — lets the creator pin a custom
 * pattern/voice directive that overrides Claude's auto-detected
 * pattern. Saving (or clearing) the directive invalidates the cached
 * 20-idea batch server-side and re-rolls the bank.
 */
function VoicePanel({
  voice, savedVoice, open, saving,
  onChange, onToggle, onSave, onClear,
}) {
  const hasSaved = Boolean((savedVoice || "").trim());
  const dirty = (voice || "").trim() !== (savedVoice || "").trim();
  return (
    <div
      data-testid="idea-bank-voice-panel"
      className="mx-6 mt-5 border border-zinc-800 bg-black/40 rounded-sm"
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid="idea-bank-voice-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-zinc-900/40 transition-colors"
      >
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400 mb-0.5 flex items-center gap-2">
            // CHANNEL VOICE / THEME OVERRIDE
            {hasSaved && (
              <span
                data-testid="idea-bank-voice-active-badge"
                className="text-[9px] tracking-normal normal-case px-1.5 py-0.5 rounded-sm bg-emerald-400/15 border border-emerald-400/40 text-emerald-300"
              >
                ACTIVE
              </span>
            )}
          </div>
          <div className="text-xs text-zinc-400 truncate">
            {hasSaved
              ? savedVoice
              : "Pin a directive (e.g. \"every idea must teach 3 rules\") to steer the next 20 ideas."}
          </div>
        </div>
        <span
          className="font-mono text-emerald-400 text-base shrink-0"
          aria-hidden
        >
          {open ? "▾" : "▸"}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-zinc-800/60">
          <textarea
            data-testid="idea-bank-voice-input"
            value={voice}
            onChange={(e) => onChange(e.target.value.slice(0, 1000))}
            placeholder='e.g. "Every video MUST follow the format: I teach my baby 3 rules of <topic> — keep the warm, narrator-led tone."'
            rows={4}
            className="w-full bg-black border border-zinc-800 focus:border-emerald-400/60 focus:outline-none rounded-sm px-3 py-2 text-sm text-zinc-100 font-sans leading-relaxed resize-y"
          />
          <div className="flex items-center justify-between mt-2 gap-3">
            <div className="font-mono text-[10px] text-zinc-500">
              {(voice || "").length}/1000 · overrides Claude&apos;s auto-detected pattern
            </div>
            <div className="flex items-center gap-2">
              {hasSaved && (
                <button
                  type="button"
                  data-testid="idea-bank-voice-clear"
                  onClick={onClear}
                  disabled={saving}
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-red-400 disabled:text-zinc-600 transition-colors px-2 py-1.5"
                >
                  Clear
                </button>
              )}
              <button
                type="button"
                data-testid="idea-bank-voice-save"
                onClick={onSave}
                disabled={saving || !dirty}
                className="font-mono text-[10px] uppercase tracking-[0.25em] bg-emerald-400 text-black hover:bg-emerald-300 disabled:bg-zinc-800 disabled:text-zinc-500 px-3 py-1.5 rounded-sm transition-colors"
              >
                {saving ? "saving…" : "Save & re-roll"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
